import { useState } from 'react'
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom'
import { Navbar } from '@/components/navbar'
import { 
  LayoutDashboard, 
  Bookmark, 
  Settings, 
  CreditCard,
  Activity,
  Rss,
  User,
  ChevronRight,
  Bell,
  Receipt,
  Menu,
  X
} from 'lucide-react'
import { isAuthenticated } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'

// Dashboard pages
import { DashboardOverview } from '@/routes/dashboard/overview'
import { DashboardBookmarks } from '@/routes/dashboard/bookmarks'
import { DashboardActivity } from '@/routes/dashboard/activity'
import { DashboardFeed } from '@/routes/dashboard/feed'
import { DashboardSettings } from '@/routes/dashboard/settings'
import { DashboardSubscription } from '@/routes/dashboard/subscription'
import { DashboardPayments } from '@/routes/dashboard/payments'
import { DashboardTransactions } from '@/routes/dashboard/transactions'
import { DashboardNotifications } from '@/routes/dashboard/notifications'
import { DashboardAccount } from '@/routes/dashboard/account'

function DashboardSidebar({ isMobileMenuOpen, setIsMobileMenuOpen }) {
  const location = useLocation()
  
  const menuItems = [
    { path: '/dashboard', label: 'Overview', icon: LayoutDashboard, exact: true },
    { path: '/dashboard/feed', label: 'My Feed', icon: Rss },
    { path: '/dashboard/bookmarks', label: 'Bookmarks', icon: Bookmark },
    { path: '/dashboard/subscription', label: 'Subscription', icon: CreditCard },
    { path: '/dashboard/transactions', label: 'Transactions', icon: Receipt },
    { path: '/dashboard/settings', label: 'Settings', icon: Settings },
  ]

  const isActive = (path, exact) => {
    if (exact) {
      return location.pathname === path
    }
    return location.pathname.startsWith(path)
  }

  const handleLinkClick = () => {
    if (setIsMobileMenuOpen) {
      setIsMobileMenuOpen(false)
    }
  }

  const sidebarContent = (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-8">
        <div data-logo-box className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300">
          <User className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">John Doe</h3>
          <p className="text-sm text-gray-500">Premium Member</p>
        </div>
      </div>

      <nav className="space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon
          const active = isActive(item.path, item.exact)
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleLinkClick}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                active
                  ? 'bg-slate-50 text-primary font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="flex-1">{item.label}</span>
              {active && <ChevronRight className="w-4 h-4" />}
            </Link>
          )
        })}
      </nav>
    </div>
  )

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 min-h-screen">
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            />
            
            {/* Sidebar */}
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed left-0 top-0 bottom-0 w-64 bg-white dark:bg-gray-800 z-50 lg:hidden overflow-y-auto"
            >
              {/* Close button */}
              <div className="flex justify-end p-4">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

function MobileMenuButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="lg:hidden fixed bottom-6 right-6 z-30 p-4 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-colors"
    >
      <Menu className="w-6 h-6" />
    </button>
  )
}

export function DashboardPage() {
  const authenticated = isAuthenticated()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  if (!authenticated) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      
      <div className="flex">
        <DashboardSidebar 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
        
        <main className="flex-1 p-4 md:p-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Routes>
              <Route index element={<DashboardOverview />} />
              <Route path="feed" element={<DashboardFeed />} />
              <Route path="bookmarks" element={<DashboardBookmarks />} />
              <Route path="subscription" element={<DashboardSubscription />} />
              <Route path="transactions" element={<DashboardTransactions />} />
              <Route path="settings" element={<DashboardSettings />} />
              {/* Redirects for old URLs */}
              <Route path="activity" element={<Navigate to="/dashboard" replace />} />
              <Route path="notifications" element={<Navigate to="/dashboard/settings?tab=notifications" replace />} />
              <Route path="account" element={<Navigate to="/dashboard/settings?tab=account" replace />} />
              <Route path="payments" element={<Navigate to="/dashboard/settings?tab=account" replace />} />
            </Routes>
          </motion.div>
        </main>
      </div>
      
      {/* Mobile Menu Button */}
      <MobileMenuButton onClick={() => setIsMobileMenuOpen(true)} />
    </div>
  )
}
